import React, { useCallback, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';
import { Gwei } from '../../lib/gwei';
import { BaseSlider, BaseSliderProps, SliderConfig } from './BaseSlider';
import { getNotch } from './utils';

interface LeverageSliderProps extends BaseSliderProps {
  min?: bigint;
  max?: bigint;
}

const DEFAULT_MIN_LEVERAGE = 1;
const DEFAULT_MAX_LEVERAGE = 50;
const DEFAULT_NOTCHES = [DEFAULT_MIN_LEVERAGE, 3, 5, 10, 25, DEFAULT_MAX_LEVERAGE];
const NUM_STEPS_BETWEEN_NOTCHES = 10;

export const LeverageSlider: React.FC<LeverageSliderProps> = ({
  max = Gwei.toGwei(DEFAULT_MAX_LEVERAGE),
  min = Gwei.toGwei(DEFAULT_MIN_LEVERAGE),
  ...props
}) => {
  const maxLeverage = Gwei.fromGwei(max);
  const minLeverage = Gwei.fromGwei(min);

  const notches = useMemo(() => {
    const filteredNotches = DEFAULT_NOTCHES.filter(
      (notch) => notch <= maxLeverage && notch >= minLeverage
    );
    if (!filteredNotches.includes(maxLeverage)) {
      filteredNotches.push(maxLeverage);
    }
    return filteredNotches.sort((a, b) => a - b);
  }, [maxLeverage, minLeverage]);

  const totalSteps = useMemo(() => (notches.length - 1) * NUM_STEPS_BETWEEN_NOTCHES, [notches]);

  const mapSliderToLeverage = useCallback(
    (val: number): number => {
      const notchIdx = Math.floor(val / NUM_STEPS_BETWEEN_NOTCHES);
      const currNotchValue = getNotch(notches, notchIdx);
      const stepNumber = val % NUM_STEPS_BETWEEN_NOTCHES;
      const nextNotchValue = notches[notchIdx + 1] || 100;
      const stepSize = (nextNotchValue - currNotchValue) / NUM_STEPS_BETWEEN_NOTCHES;
      return parseFloat((currNotchValue + stepSize * stepNumber).toFixed(2));
    },
    [notches]
  );

  const mapLeverageToSlider = useCallback(
    (leverage: number): number => {
      if (leverage <= DEFAULT_MIN_LEVERAGE) return 0;

      for (let intervalIndex = 1; intervalIndex < notches.length; intervalIndex++) {
        const lowerNotch = getNotch(notches, intervalIndex - 1);
        const upperNotch = getNotch(notches, intervalIndex);

        if (leverage > lowerNotch && leverage <= upperNotch) {
          const leverageRange = upperNotch - lowerNotch;
          const stepSize = leverageRange / NUM_STEPS_BETWEEN_NOTCHES;
          const stepsFromLowerNotch = Math.round((leverage - lowerNotch) / stepSize);
          const basePosition = (intervalIndex - 1) * NUM_STEPS_BETWEEN_NOTCHES;
          return basePosition + stepsFromLowerNotch;
        }
      }

      return totalSteps;
    },
    [notches, totalSteps]
  );

  const config: SliderConfig = useMemo(() => {
    const ticks = notches.map((notch) => ({
      value: notch,
      label: `${notch}x`,
    }));

    return {
      min: 0,
      max: totalSteps,
      step: 1,
      ticks,
      normalizeValue: (val: bigint) => mapLeverageToSlider(Gwei.fromGwei(val)),
      denormalizeValue: (val: number) => Gwei.toGwei(mapSliderToLeverage(val)),
      getPercentage: (normalizedValue: number) => normalizedValue / totalSteps,
    };
  }, [notches, totalSteps, mapLeverageToSlider, mapSliderToLeverage]);

  const renderTickLabel = useCallback(
    (tick: { value: number; label?: string }, _index: number, isHovered: boolean | null) => (
      <div
        className={twJoin(
          'text-[10px]',
          isHovered ? 'text-contentPrimary' : 'text-contentSecondary'
        )}
      >
        {tick.label}
      </div>
    ),
    []
  );

  return (
    <BaseSlider {...props} config={config} showLabels={false} renderTickLabel={renderTickLabel} />
  );
};
